import React from 'react';
import { format } from 'date-fns';
import { useDroppable } from '@dnd-kit/core';
import { formatCustomDate } from '../../../utils/dateFormatters';
import {
  DayContainer,
  DayColumnHeader,
  DayColumnHeaderContent,
  DayLabel,
  ExpectedHours,
  IsTodayLabel,
  DayColumnBody,
  DayHeaderNavButton,
} from '../styled';
import { DraggableEvent } from './DraggableEvent';
import { OccurrenceDraggableEvent } from './OccurrenceDraggableEvent';
import { ArrowLeftIcon } from './ArrowLeftIcon';
import { ArrowRightIcon } from './ArrowRightIcon';
import { CalendarEvent, EventColorSet } from 'pages/Agenda/utils';
import { useDayColumn } from '../../../hooks/useDayColumn';

interface DayColumnProps {
  date: Date;
  events: CalendarEvent[];
  horasPrevistasLabel: string;
  dayIndex: number;
  onCardHover: (data: { event: CalendarEvent; rect: DOMRect; colorSet: EventColorSet }) => void;
  onCardLeave: () => void;
  onCardClick: (event: CalendarEvent, colorSet: EventColorSet) => void;
  isDragging: boolean;
  getColorSet: (event: CalendarEvent) => EventColorSet;
  isCompletedView: boolean;
  onNavigateByDay?: (direction: 'prev' | 'next') => void;
}

export const DayColumn: React.FC<DayColumnProps> = ({
  date,
  events,
  horasPrevistasLabel,
  dayIndex,
  onCardHover,
  onCardLeave,
  onCardClick,
  isDragging,
  getColorSet,
  isCompletedView,
  onNavigateByDay,
}) => {
  const { setNodeRef, isOver } = useDroppable({
    id: format(date, 'yyyy-MM-dd'),
  });

  const { isToday, isTomorrowDate, isWeekendDay, showLeftArrow, showRightArrow } = useDayColumn({
    date,
    dayIndex,
    onNavigateByDay,
  });

  return (
    <DayContainer
      ref={setNodeRef}
      isFirst={dayIndex === 0}
      isLast={dayIndex === 6}
      isHighlighted={isToday}
      removeLeftBorder={isTomorrowDate}
      isWeekend={isWeekendDay}
      isDropTarget={isOver}
    >
      {isToday && <IsTodayLabel $hasLeftArrow={showLeftArrow}>Hoje</IsTodayLabel>}
      <DayColumnHeader $hasLeftArrow={showLeftArrow} $hasRightArrow={showRightArrow}>
        {showLeftArrow && (
          <DayHeaderNavButton
            type="button"
            side="left"
            onClick={() => onNavigateByDay?.('prev')}
            aria-label="Voltar um dia"
          >
            <ArrowLeftIcon />
          </DayHeaderNavButton>
        )}
        <DayColumnHeaderContent $grow={showLeftArrow || showRightArrow}>
          <DayLabel isHighlighted={isToday} isBlack={date.getTime() > new Date().getTime()}>
            {formatCustomDate(date)}
          </DayLabel>
          <ExpectedHours>{horasPrevistasLabel} previstas</ExpectedHours>
        </DayColumnHeaderContent>
        {showRightArrow && (
          <DayHeaderNavButton
            type="button"
            side="right"
            onClick={() => onNavigateByDay?.('next')}
            aria-label="Avançar um dia"
          >
            <ArrowRightIcon />
          </DayHeaderNavButton>
        )}
      </DayColumnHeader>
      <DayColumnBody>
        {events.map(event =>
          event.eventKind === 'ocorrencia' ? (
            <OccurrenceDraggableEvent
              key={event.id}
              event={event}
              onHover={onCardHover}
              onLeave={onCardLeave}
              onCardClick={onCardClick}
              isDragging={isDragging}
              colorSet={getColorSet(event)}
              isCompletedView={isCompletedView}
            />
          ) : (
            <DraggableEvent
              key={event.id}
              event={event}
              onHover={onCardHover}
              onLeave={onCardLeave}
              onCardClick={onCardClick}
              isDragging={isDragging}
              colorSet={getColorSet(event)}
              isCompletedView={isCompletedView}
            />
          ),
        )}
      </DayColumnBody>
    </DayContainer>
  );
};
